import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export const THEMES = [
  { id: 'indigo',   label: 'Indigo',   primary: '#6366f1', secondary: '#8b5cf6', rgb: '99,102,241'   },
  { id: 'blue',     label: 'Blue',     primary: '#3b82f6', secondary: '#60a5fa', rgb: '59,130,246'   },
  { id: 'violet',   label: 'Violet',   primary: '#7c3aed', secondary: '#a78bfa', rgb: '124,58,237'   },
  { id: 'rose',     label: 'Rose',     primary: '#f43f5e', secondary: '#fb7185', rgb: '244,63,94'    },
  { id: 'orange',   label: 'Orange',   primary: '#f97316', secondary: '#fb923c', rgb: '249,115,22'   },
  { id: 'amber',    label: 'Amber',    primary: '#d97706', secondary: '#fbbf24', rgb: '217,119,6'    },
  { id: 'emerald',  label: 'Emerald',  primary: '#10b981', secondary: '#34d399', rgb: '16,185,129'   },
  { id: 'teal',     label: 'Teal',     primary: '#0d9488', secondary: '#2dd4bf', rgb: '13,148,136'   },
  { id: 'cyan',     label: 'Cyan',     primary: '#0891b2', secondary: '#22d3ee', rgb: '8,145,178'    },
  { id: 'slate',    label: 'Slate',    primary: '#475569', secondary: '#64748b', rgb: '71,85,105'    },
  { id: 'pink',     label: 'Pink',     primary: '#db2777', secondary: '#f472b6', rgb: '219,39,119'   },
  { id: 'custom',   label: 'Custom',   primary: null,      secondary: null,      rgb: null           },
];

const ThemeContext = createContext();

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

// Darken a hex color by a percentage
function darken(hex, pct = 15) {
  let r = parseInt(hex.slice(1,3),16);
  let g = parseInt(hex.slice(3,5),16);
  let b = parseInt(hex.slice(5,7),16);
  r = Math.max(0, Math.round(r * (1 - pct/100)));
  g = Math.max(0, Math.round(g * (1 - pct/100)));
  b = Math.max(0, Math.round(b * (1 - pct/100)));
  return '#' + [r,g,b].map(x=>x.toString(16).padStart(2,'0')).join('');
}

function applyTheme(primary, secondary) {
  const root = document.documentElement;
  const rgb  = hexToRgb(primary);
  root.style.setProperty('--pm-primary',          primary);
  root.style.setProperty('--pm-secondary',        secondary);
  root.style.setProperty('--pm-primary-dark',     darken(primary, 12));
  root.style.setProperty('--pm-gradient',         `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`);
  root.style.setProperty('--pm-primary-rgb',      rgb);
  root.style.setProperty('--pm-primary-light',    `rgba(${rgb}, 0.12)`);
  root.style.setProperty('--pm-primary-lighter',  `rgba(${rgb}, 0.06)`);

  // ── Inject global CSS override so hardcoded #6366f1 colors in JSX/CSS
  //    are replaced with theme variables everywhere ────────────────────
  let el = document.getElementById('pm-theme-override');
  if (!el) { el = document.createElement('style'); el.id = 'pm-theme-override'; document.head.appendChild(el); }

  el.textContent = `
    /* ── All gradient buttons / backgrounds ─────────────────────── */
    [style*="linear-gradient(135deg, #6366f1"],
    [style*="linear-gradient(135deg,#6366f1"],
    [style*="background: linear-gradient(135deg, #6366f1"],
    [style*="background:linear-gradient(135deg,#6366f1"] {
      background: linear-gradient(135deg, ${primary} 0%, ${secondary} 100%) !important;
    }

    /* ── Solid primary color backgrounds ────────────────────────── */
    [style*="background: #6366f1"],
    [style*="background:#6366f1"],
    [style*="background-color: #6366f1"],
    [style*="background-color:#6366f1"] {
      background-color: ${primary} !important;
      background: ${primary} !important;
    }
    [style*="background: #8b5cf6"],
    [style*="background:#8b5cf6"] {
      background: ${secondary} !important;
    }

    /* ── Text color ──────────────────────────────────────────────── */
    [style*="color: #6366f1"],
    [style*="color:#6366f1"] {
      color: ${primary} !important;
    }
    [style*="color: #8b5cf6"],
    [style*="color:#8b5cf6"] {
      color: ${secondary} !important;
    }

    /* ── Border color ────────────────────────────────────────────── */
    [style*="border-color: #6366f1"],
    [style*="border-color:#6366f1"],
    [style*="border: 1.5px solid #6366f1"],
    [style*="border: 2px solid #6366f1"],
    [style*="border:1.5px solid #6366f1"],
    [style*="border:2px solid #6366f1"] {
      border-color: ${primary} !important;
    }

    /* ── Box shadow with primary color ───────────────────────────── */
    [style*="6366f133"],
    [style*="6366f144"],
    [style*="6366f122"],
    [style*="6366f11a"] {
      box-shadow: 0 4px 14px rgba(${rgb}, 0.3) !important;
    }

    /* ── Header active indicator ─────────────────────────────────── */
    .nav-item.active,
    .nav-item.active:hover {
      background: rgba(${rgb}, 0.2) !important;
      box-shadow: inset 0 0 0 1px rgba(${rgb}, 0.3) !important;
    }
    .logo-icon,
    .user-avatar {
      background: linear-gradient(135deg, ${primary} 0%, ${secondary} 100%) !important;
      box-shadow: 0 4px 12px rgba(${rgb}, 0.4) !important;
    }

    /* ── CSS class-based buttons in CSS files ────────────────────── */
    .btn-primary,
    button.primary {
      background: linear-gradient(135deg, ${primary} 0%, ${secondary} 100%) !important;
    }
  `;
}

function applyDarkMode(enabled) {
  const root = document.documentElement;
  let el = document.getElementById('pm-dark-override');
  if (!el) { el = document.createElement('style'); el.id = 'pm-dark-override'; document.head.appendChild(el); }
  if (enabled) {
    root.setAttribute('data-dark', 'true');
    el.textContent = `
      body, #root { background: #0f172a !important; color: #e2e8f0 !important; }
      [style*="background: #fff"], [style*="background:#fff"],
      [style*="background: white"], [style*="background:white"] { background: #1e293b !important; }
      [style*="background: #f8fafc"], [style*="background:#f8fafc"],
      [style*="background: #fafbfc"], [style*="background:#fafbfc"],
      [style*="background: #f1f5f9"], [style*="background:#f1f5f9"] { background: #1e293b !important; }
      [style*="color: #0f172a"], [style*="color:#0f172a"] { color: #f1f5f9 !important; }
      [style*="color: #1e293b"], [style*="color:#1e293b"] { color: #e2e8f0 !important; }
      [style*="color: #334155"], [style*="color:#334155"] { color: #cbd5e1 !important; }
      [style*="color: #475569"], [style*="color:#475569"] { color: #94a3b8 !important; }
      [style*="color: #64748b"], [style*="color:#64748b"] { color: #64748b !important; }
      [style*="border: 1px solid #f1f5f9"], [style*="border:1px solid #f1f5f9"],
      [style*="border: 1.5px solid #e2e8f0"], [style*="border:1.5px solid #e2e8f0"],
      [style*="border: 2px solid #e2e8f0"], [style*="border:2px solid #e2e8f0"],
      [style*="border-bottom: 1px solid #f1f5f9"] { border-color: #334155 !important; }
      [style*="box-shadow: 0 2px 12px #0000000a"],
      [style*="box-shadow: 0 2px 16px #0000000a"] { box-shadow: 0 2px 12px #00000040 !important; }
      input, select, textarea { background: #1e293b !important; color: #e2e8f0 !important; border-color: #334155 !important; }
      table thead tr { background: #1e293b !important; }
      .sidebar { background: #0f172a !important; border-color: #1e293b !important; }
    `;
  } else {
    root.removeAttribute('data-dark');
    el.textContent = '';
  }
}

export function ThemeProvider({ children }) {
  const saved      = localStorage.getItem('pmThemeId')     || 'indigo';
  const savedCustom = localStorage.getItem('pmThemeCustom') || null;
  const savedDark   = localStorage.getItem('pmDarkMode') === 'true';

  const [themeId,      setThemeId]      = useState(saved);
  const [customColor,  setCustomColor]  = useState(savedCustom || '#6366f1');
  const [darkMode,     setDarkModeState] = useState(savedDark);

  const activeTheme = THEMES.find(t => t.id === themeId) || THEMES[0];

  useEffect(() => {
    const primary   = themeId === 'custom' ? customColor   : activeTheme.primary;
    const secondary = themeId === 'custom' ? darken(customColor, -20) : activeTheme.secondary;
    applyTheme(primary, secondary);
  }, [themeId, customColor, activeTheme]);

  useEffect(() => { applyDarkMode(darkMode); }, [darkMode]);

  const toggleDarkMode = useCallback(() => {
    setDarkModeState(prev => {
      const next = !prev;
      localStorage.setItem('pmDarkMode', next);
      api.put('/auth/profile', { darkMode: next }).catch(() => {});
      return next;
    });
  }, []);

  const setTheme = useCallback((id, save = true) => {
    setThemeId(id);
    localStorage.setItem('pmThemeId', id);
    if (save) {
      api.put('/auth/profile', { themeId: id }).catch(() => {});
    }
  }, []);

  const setCustomTheme = useCallback((hex, save = true) => {
    setCustomColor(hex);
    setThemeId('custom');
    localStorage.setItem('pmThemeId', 'custom');
    localStorage.setItem('pmThemeCustom', hex);
    if (save) {
      api.put('/auth/profile', { themeId: 'custom', themeCustom: hex }).catch(() => {});
    }
  }, []);

  // Listen for theme sync after login (dispatched by AuthContext)
  useEffect(() => {
    const handler = (e) => {
      const { themeId: tid, themeCustom: tc } = e.detail || {};
      if (tid) {
        localStorage.setItem('pmThemeId', tid);
        setThemeId(tid);
      }
      if (tc) {
        localStorage.setItem('pmThemeCustom', tc);
        setCustomColor(tc);
      }
    };
    window.addEventListener('pm-theme-sync', handler);
    return () => window.removeEventListener('pm-theme-sync', handler);
  }, []);

  return (
    <ThemeContext.Provider value={{ themeId, setTheme, customColor, setCustomTheme, activeTheme, darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
